import { z } from 'zod';
import { DiseaseRiskStatusEnum } from '../regional-overview/constants.ts';

export const diseaseRiskStatusSchema = z.object({
  error: z.string().nullable().optional(),
  error_code: z.string().nullable().optional(),
  error_original: z.string().nullable().optional(),
  processedAt: z.string().datetime({ offset: true, local: true }),
  requestedAt: z.string().datetime({ offset: true, local: true }),
  status: z.nativeEnum(DiseaseRiskStatusEnum).optional()
});

const riskBackendSchema = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]);

const isoDateSchema = z.string().datetime({ offset: true, local: true });

const dailyRiskBaseSchema = z.object({
  date: isoDateSchema,
  risk: riskBackendSchema
});

export const dailyRiskSchema = dailyRiskBaseSchema.extend({
  leafInformation: z
    .object({
      incubation: z.array(z.string()),
      // BFF spelling: 'sympton' (not 'symptom') — preserved to match wire.
      sympton: z.array(z.string())
    })
    .optional(),
  messageLabel: z.string().optional(),
  plantBBCHNum: z.number(),
  disease: z.string().optional()
});

export const diseaseInfoSchema = z.object({
  dates: z.array(dailyRiskSchema),
  disease: z.string(),
  risk: riskBackendSchema
});

export const diseaseRiskSimulationSchema = z.object({
  simDateCalculated: isoDateSchema,
  simId: z.number()
});

// BFF invariant: a field belongs to exactly one crop, so `cropId` is always a single-element array.
const cropIdSchema = z.tuple([z.string()]);

export const diseaseRiskFieldDataDtoSchema = z.object({
  cropId: cropIdSchema,
  diseases: z.array(diseaseInfoSchema),
  fieldId: z.string().uuid(),
  risk: riskBackendSchema,
  status: diseaseRiskStatusSchema,
  simulation: diseaseRiskSimulationSchema.nullable()
});

export const diseaseRisksDtoSchema = z.object({
  fields: z.array(diseaseRiskFieldDataDtoSchema)
});

export const diseaseInfoPotatoTreatmentAdviceSchema = z.object({
  adviceMessageSlug: z.string().nullable().optional(),
  adviceProductType: z.string().nullable(),
  adviceTreatmentDate: isoDateSchema.nullable(),
  productPersistance: z.number().nullable().optional()
});

export const diseasePotatoDataDtoSchema = dailyRiskBaseSchema.extend({
  protection: z.boolean(),
  contaminationWeight: z.number().nullable(),
  sporulationPotential: z.number().nullable(),
  sporeReserve: z.number().nullable(),
  contaminationIndex: z.number().nullable(),
  rainfastness: z.number().nullable(),
  rainfallAmount: z.number().nullable(),
  isPast: z.boolean(),
  updatedAt: isoDateSchema.nullable()
});

export const diseaseInfoPotatoSchema = z.object({
  disease: z.string(),
  todayData: z.array(diseasePotatoDataDtoSchema),
  tomorrowData: z.array(diseasePotatoDataDtoSchema),
  sixDaysData: z.array(diseasePotatoDataDtoSchema),
  risk: riskBackendSchema,
  treatmentAdvice: diseaseInfoPotatoTreatmentAdviceSchema
});

export const diseaseRiskFieldDataPotatoDtoSchema = z.object({
  cropId: cropIdSchema,
  diseases: z.array(diseaseInfoPotatoSchema),
  fieldId: z.string().uuid(),
  risk: riskBackendSchema,
  status: diseaseRiskStatusSchema
});

export const diseaseRisksPotatoDtoSchema = z.object({
  fields: z.array(diseaseRiskFieldDataPotatoDtoSchema)
});
